/**
 * Build the POST /csv/import request body from the flow's state. Pure; this
 * is the single place that knows the admin vs client payload difference.
 */
import type { CsvImportRequest } from '@shared/schemas';

import { detectSource } from '../../_shared/domain/file-import';
import { type DataSourceDraft, toDataSourcePayload } from './data-source';

/** The request body, plus the pre-enriched flag the endpoint accepts. */
export type FileImportRequest = CsvImportRequest & { is_enriched?: boolean };

export interface BuildImportRequestInput {
  /** Admin mode: the existing list to import into. Client mode: null. */
  listId: string | null;
  /** Client mode: the name of the list created by the import. */
  importName: string;
  /** Source file name; drives the client-mode `source` label and is sent for Invyo's alert (#748). */
  fileName: string;
  columnMapping: Record<string, string>;
  rows: Record<string, string>[];
  isEnriched: boolean;
  /** Admin mode (#709): the conflicting rows to overwrite; the backend ignores it from a client. */
  overwriteRows?: number[];
  /** Client mode (#749): where the file's data came from. Never sent with a listId. */
  dataSource: DataSourceDraft;
}

export function buildImportRequest({
  listId,
  importName,
  fileName,
  columnMapping,
  rows,
  isEnriched,
  overwriteRows = [],
  dataSource,
}: BuildImportRequestInput): FileImportRequest {
  const dataSourcePayload = listId ? null : toDataSourcePayload(dataSource);
  return {
    column_mapping: columnMapping,
    rows,
    ...(listId
      ? { list_id: listId }
      : {
          import_name: importName.trim(),
          source: detectSource(fileName),
          // Shown to Invyo in the alert about the new import (#748); not stored.
          ...(fileName ? { file_name: fileName } : {}),
          ...(dataSourcePayload ? { data_source: dataSourcePayload } : {}),
        }),
    ...(isEnriched ? { is_enriched: true } : {}),
    ...(listId && overwriteRows.length ? { overwrite_rows: overwriteRows } : {}),
  };
}
